import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { Layout, PageHero } from '@components';
import { email, contacts } from '@config';

const StyledSection = styled.section`
  ${({ theme }) => theme.mixins.section};
  background: var(--paper);
`;

const StyledInner = styled.div`
  ${({ theme }) => theme.mixins.container};
  display: grid;
  grid-template-columns: 1fr 0.9fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StyledCard = styled.div`
  ${({ theme }) => theme.mixins.card};
  padding: 40px 36px;

  .label {
    margin: 0 0 8px;
    color: var(--text-mute);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .value {
    display: inline-block;
    margin-bottom: 30px;
    color: var(--text);
    font-size: clamp(1.2rem, 2.4vw, 1.6rem);
    font-weight: 600;
    word-break: break-all;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StyledForm = styled.form`
  margin-top: 30px;
  padding-top: 28px;
  border-top: 1px solid var(--line);

  .intro {
    margin: 0 0 20px;
    color: var(--text-soft);
    font-size: var(--fz-xs);
    line-height: 1.6;

    a {
      color: var(--blue);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  .consent-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;

    input {
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
      margin-top: 1px;
      accent-color: var(--blue);
      cursor: pointer;
    }

    label {
      color: var(--text);
      font-size: var(--fz-sm);
      line-height: 1.5;
      cursor: pointer;
      user-select: none;
    }
  }

  .cta {
    ${({ theme }) => theme.mixins.primaryButton};
    width: 100%;
    margin-top: 20px;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
      transform: none;
    }
    &:disabled:hover,
    &:disabled:focus {
      transform: none;
      box-shadow: none;
    }
  }

  .hint {
    margin: 12px 0 0;
    color: var(--text-mute);
    font-size: var(--fz-xxs);
    line-height: 1.5;
    text-align: center;
  }
`;

const StyledAside = styled.div`
  h2 {
    font-size: var(--fz-xxl);
  }

  ol {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    gap: 18px;
    counter-reset: step;
    margin: 24px 0 0;

    li {
      position: relative;
      padding-left: 44px;
      color: var(--text-soft);
      font-size: var(--fz-sm);
      line-height: 1.6;
      counter-increment: step;

      &:before {
        content: counter(step);
        ${({ theme }) => theme.mixins.flexCenter};
        position: absolute;
        left: 0;
        top: 0;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: var(--gradient);
        color: #ffffff;
        font-size: var(--fz-xs);
        font-weight: 700;
      }
    }
  }
`;

const ContactsPage = ({ location }) => {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const canSend = privacyAccepted && consentAccepted;

  const handleSubmit = event => {
    event.preventDefault();
    if (!canSend) {
      return;
    }
    window.location.href = `mailto:${email}`;
  };

  return (
    <Layout
      location={location}
      title="Контакты"
      description="Свяжитесь со специалистом SMART-KVADRAT для обсуждения проекта, оценки задачи и консультации по разработке.">
      <main>
        <PageHero
          eyebrow="Контакты"
          title="Свяжитесь со мной"
          lead="Напишите о своей задаче — отвечу и предложу решение. Ниже — актуальные контактные данные."
        />

        <StyledSection>
          <StyledInner>
            <StyledCard>
              <p className="label">Электронная почта</p>
              <span className="value">{email}</span>

              <p className="label">Город</p>
              <span className="value">{contacts.location}</span>

              <StyledForm onSubmit={handleSubmit} noValidate>
                <p className="intro">
                  Отправляя письмо, вы передаёте данные по своей инициативе. Обработка персональных
                  данных осуществляется в соответствии с{' '}
                  <Link to="/personal-data">документом об обработке персональных данных</Link> и{' '}
                  <Link to="/privacy">политикой конфиденциальности</Link>.
                </p>

                <div className="consent-row">
                  <input
                    id="consent-privacy"
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={e => setPrivacyAccepted(e.target.checked)}
                    required
                  />
                  <label htmlFor="consent-privacy">
                    Я ознакомлен(а) с Политикой конфиденциальности
                  </label>
                </div>

                <div className="consent-row">
                  <input
                    id="consent-pd"
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={e => setConsentAccepted(e.target.checked)}
                    required
                  />
                  <label htmlFor="consent-pd">
                    Я даю согласие на обработку персональных данных
                  </label>
                </div>

                <button className="cta" type="submit" disabled={!canSend}>
                  Написать письмо
                </button>

                {!canSend && (
                  <p className="hint">
                    Чтобы отправить письмо, отметьте оба пункта согласия.
                  </p>
                )}
              </StyledForm>
            </StyledCard>

            <StyledAside>
              <h2>Как начать работу</h2>
              <ol>
                <li>Опишите задачу или идею в письме — в свободной форме.</li>
                <li>Уточним требования, объём и ориентировочные сроки.</li>
                <li>Предложу решение и план работ по проекту.</li>
              </ol>
            </StyledAside>
          </StyledInner>
        </StyledSection>
      </main>
    </Layout>
  );
};

ContactsPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default ContactsPage;
